import { COMPANY, GRIEVANCE } from '@siva/shared';
import { t } from '../../i18n/index.js';

/**
 * The privacy policy, public and reachable without signing in.
 *
 * Google Play requires one at a URL anybody can open before it will accept the Data safety form,
 * and the app asks for location, so there is no way around it. The Digital Personal Data
 * Protection Act, 2023 wants the same notice for its own reasons.
 *
 * The prose lives here rather than in the translation file on purpose. A privacy policy is one
 * document that has to be read whole and has to be true; split across thirty keys it becomes
 * thirty sentences that can each be edited without anyone reading the result. The company's
 * registered details come from the shared constant the documents print, so the policy and a pay
 * slip can never name the company two different ways.
 *
 * Every claim below was checked against what the system actually does. If the code changes so
 * that one of them stops being true, this page is part of that change.
 */
export function PrivacyPage() {
  const updated = '19 September 2026';
  return (
    <article className="card policy" aria-label={t('privacy_title')}>
      <h1>{t('privacy_title')}</h1>
      <p className="muted">{t('privacy_updated', { date: updated })}</p>

      <p>
        This policy explains what the SIVA Attendance app records about you, why, and what you can
        do about it. It applies to the app and to the attendance system behind it.
      </p>

      <h2>Who collects it</h2>
      <p>
        {COMPANY.name}
        <br />
        {COMPANY.addressLines.map((line) => (
          <span key={line}>
            {line}
            <br />
          </span>
        ))}
        Phone: {COMPANY.phone}
        <br />
        GSTIN: {COMPANY.gstin}
      </p>
      <p>
        The company is the data fiduciary for this information. It is collected because you are
        engaged by the company, and it is used to run your attendance and your pay.
      </p>

      <h2>What is collected</h2>
      <ul>
        <li>
          <strong>Who you are</strong> — your name, phone number, employee code, job title,
          joining date and whether you are full-time or part-time.
        </li>
        <li>
          <strong>Your attendance</strong> — the time of every punch in and out, the hours that
          follow from them, your weekly off days, and any correction an admin makes to a day,
          with the reason they gave.
        </li>
        <li>
          <strong>Where you were when you punched</strong> — the position your phone reported, how
          accurate it said that position was, and how far it was from the office.
        </li>
        <li>
          <strong>What device you punched from</strong> — the app version, a security check result
          from Google Play about whether the device is genuine and unmodified, whether the phone
          reported a simulated location, the network address the request came from, and the
          browser or app identification your phone sends.
        </li>
        <li>
          <strong>Your bank details and PAN</strong>, if you give them — bank name, account
          number, IFSC and PAN. These are used only to print your own pay slip.
        </li>
      </ul>

      <h2>Location, specifically</h2>
      <p>
        Your location is read <strong>only at the moment you tap to punch in or out</strong>. It is
        never read in the background, never while the app is closed, and never at any other time.
        It is compared with the office location to decide whether the punch can be accepted, and
        the position is stored with that punch as the record of where it was made.
      </p>

      <h2>Why it is collected</h2>
      <ul>
        <li>To record attendance and work out the hours you are paid for.</li>
        <li>To produce your pay slips and your employment agreement.</li>
        <li>To meet the company&rsquo;s tax, labour and record-keeping obligations.</li>
        <li>To detect attendance recorded dishonestly, such as a punch made away from the office
          or from a phone running a fake-location app.</li>
      </ul>

      <h2>Who it is shared with</h2>
      <p>
        Nobody. It is not sold, not used for advertising, and not given to any other company for
        their own purposes. There is no advertising and no analytics in this app.
      </p>
      <p>The only exceptions are the ordinary ones:</p>
      <ul>
        <li>
          Service providers who process it on the company&rsquo;s behalf and only on its
          instructions — the hosting provider that runs the server, and the service that sends
          the company&rsquo;s email.
        </li>
        <li>Where the law requires it, or a court or authority lawfully demands it.</li>
      </ul>
      <p>
        Your bank details, your PAN and your pay slips are visible to you and to the office. They
        appear on no report, in no list, and on nothing belonging to another employee.
      </p>

      <h2>How long it is kept</h2>
      <ul>
        <li>
          <strong>Attendance and payment records</strong> are kept while you are employed and
          afterwards for as long as tax and labour law require.
        </li>
        <li>
          <strong>Pay slips and employment agreements</strong> are kept permanently. They are the
          record of what was paid and what was agreed, and they cannot be altered once issued.
        </li>
        <li>
          <strong>Notification records</strong> are deleted after 90 days.
        </li>
        <li>
          <strong>Records of refused punches</strong> are deleted after 180 days, unless one is
          still attached to something an admin has not yet reviewed.
        </li>
      </ul>

      <h2>How it is protected</h2>
      <p>
        Everything travels over an encrypted connection. Signing in uses the fingerprint or face
        unlock on your own phone, so there is no password to steal, and a passkey works only on
        the phone it was set up on. When you leave the company your access ends immediately.
      </p>

      <h2>Your rights</h2>
      <p>
        Under the Digital Personal Data Protection Act, 2023 you may ask what the company holds
        about you, ask for it to be corrected, ask how it has been used, nominate someone to act
        for you, and complain if you are not satisfied. Attendance records that the company must
        keep by law cannot be erased on request, and your pay slips cannot be altered once issued
        — but a mistake in them can be corrected by issuing a corrected version, which is what the
        office does.
      </p>

      <h2>Complaints and questions</h2>
      <p>
        Speak to the office, or contact the grievance officer:
        <br />
        Email: <a href={`mailto:${GRIEVANCE.email}`}>{GRIEVANCE.email}</a>
        <br />
        Phone: <a href={`tel:${GRIEVANCE.phone.replace(/\s/g, '')}`}>{GRIEVANCE.phone}</a>
      </p>

      <h2>Children</h2>
      <p>
        This app is for people engaged by the company to work. It is not directed at children and
        is not offered to them.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy changes, the date at the top changes with it and the new version appears on
        this page.
      </p>
    </article>
  );
}
